import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Sparkles, Video, Images } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const PLANS = [
  {
    id: "basic",
    name: "Basic",
    monthly: { key: "basic_monthly", price: 9.99 },
    yearly: { key: "basic_yearly", price: 99 },
    tagline: "Starter kit for solo creators",
    limits: { videos: 5, carousels: 5 },
    features: [
      "TikTok + Instagram posting",
      "Google Drive integration",
      "3 linked social accounts",
      "Attempt logs & retries",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    monthly: { key: "pro_monthly", price: 15.99 },
    yearly: { key: "pro_yearly", price: 159 },
    tagline: "For active creators & small teams",
    limits: { videos: 20, carousels: 20 },
    features: [
      "Everything in Basic",
      "4× more daily posts",
      "Priority publishing queue",
      "Extended attempt history",
    ],
    highlight: true,
  },
  {
    id: "ultra",
    name: "Ultra",
    monthly: { key: "ultra_monthly", price: 24.99 },
    yearly: { key: "ultra_yearly", price: 249 },
    tagline: "Agencies & high-volume brands",
    limits: { videos: 50, carousels: 50 },
    features: [
      "Everything in Pro",
      "10× the daily posts",
      "Unlimited linked accounts",
      "Priority support",
    ],
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [interval, setInterval] = useState("monthly"); // "monthly" | "yearly"
  const [sub, setSub] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    (async () => {
      if (!user) return;
      try {
        const { data } = await api.get("/payments/subscription");
        setSub(data);
      } catch (e) { /* silent */ }
    })();
  }, [user]);

  const currentTier = sub?.plan_tier || "none";
  const onTrial = !!sub?.on_trial;
  const cancelable = sub?.has_subscription && !sub?.cancel_at_period_end;

  const startCheckout = async (lookupKey) => {
    if (!user) {
      navigate("/register");
      return;
    }
    setLoadingKey(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoadingKey(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel subscription? You keep access until the end of the current period.")) return;
    try {
      const { data } = await api.post("/payments/cancel");
      toast.success(data.message);
      const { data: fresh } = await api.get("/payments/subscription");
      setSub(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    }
  };

  const yearlySaving = useMemo(() => {
    // Show ~% saving on the toggle
    const monthlyTotal = 15.99 * 12;
    const yearly = 159;
    return Math.round(((monthlyTotal - yearly) / monthlyTotal) * 100);
  }, []);

  return (
    <div className="space-y-8" data-testid="pricing-page">
      <header className="text-center">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Plans</div>
        <h1 className="mt-2 font-display font-black tracking-tight text-3xl sm:text-5xl text-slate-900">
          Pick a plan that fits your output
        </h1>
        <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
          Every plan includes TikTok + Instagram publishing, Google Drive access, and our secure white-label linking.
          Cancel or switch any time.
        </p>
        {onTrial && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-3 py-1 text-xs font-medium text-blue-800" data-testid="trial-banner">
            <Sparkles className="w-3.5 h-3.5" />
            You&apos;re on the 3-day trial — pick a plan before it ends to stay published.
          </div>
        )}
      </header>

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3">
        <div className="inline-flex rounded-full border border-zinc-200 bg-white p-1" data-testid="billing-interval-toggle">
          <button
            type="button"
            onClick={() => setInterval("monthly")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              interval === "monthly" ? "bg-zinc-900 text-white" : "text-slate-700 hover:text-slate-900"
            }`}
            data-testid="interval-monthly"
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setInterval("yearly")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              interval === "yearly" ? "bg-zinc-900 text-white" : "text-slate-700 hover:text-slate-900"
            }`}
            data-testid="interval-yearly"
          >
            Yearly <Badge className="ml-1 bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">Save {yearlySaving}%</Badge>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="plans-grid">
        {PLANS.map((p) => {
          const priceObj = p[interval];
          const isCurrent = currentTier === p.id && sub?.plan_lookup_key === priceObj.key;
          return (
            <Card
              key={p.id}
              className={`relative p-6 border-zinc-200 flex flex-col ${p.highlight ? "ring-2 ring-blue-500 shadow-lg" : ""}`}
              data-testid={`plan-${p.id}`}
            >
              {p.highlight && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white hover:bg-blue-600 text-[10px]">Most popular</Badge>
              )}
              <div className="font-display text-2xl font-bold text-slate-900">{p.name}</div>
              <div className="text-sm text-slate-500 mt-1">{p.tagline}</div>
              <div className="mt-5">
                <span className="font-display text-4xl font-black tracking-tight text-slate-900">£{priceObj.price}</span>
                <span className="text-slate-500 text-sm ml-1">/ {interval === "monthly" ? "mo" : "yr"}</span>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-md bg-zinc-50 border border-zinc-100 p-2">
                  <div className="flex items-center gap-1 text-slate-500"><Video className="w-3 h-3" /> Videos / day</div>
                  <div className="mt-0.5 font-semibold text-slate-900" data-testid={`plan-${p.id}-videos`}>{p.limits.videos}</div>
                </div>
                <div className="rounded-md bg-zinc-50 border border-zinc-100 p-2">
                  <div className="flex items-center gap-1 text-slate-500"><Images className="w-3 h-3" /> Carousels / day</div>
                  <div className="mt-0.5 font-semibold text-slate-900" data-testid={`plan-${p.id}-carousels`}>{p.limits.carousels}</div>
                </div>
              </div>

              <ul className="mt-5 space-y-2 text-sm text-slate-700 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => startCheckout(priceObj.key)}
                disabled={loadingKey === priceObj.key || isCurrent}
                className={`mt-6 w-full h-11 ${p.highlight ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-zinc-900 hover:bg-zinc-800 text-white"}`}
                data-testid={`plan-${p.id}-cta`}
              >
                {isCurrent ? "Current plan" : (loadingKey === priceObj.key ? "Redirecting…" : `Choose ${p.name}`)}
              </Button>
              {isCurrent && cancelable && (
                <button type="button" onClick={cancel} className="mt-2 text-xs text-slate-500 underline underline-offset-2 hover:text-slate-700" data-testid={`plan-${p.id}-cancel`}>
                  Cancel at period end
                </button>
              )}
            </Card>
          );
        })}
      </div>

      <p className="text-center text-xs text-slate-500">Prices in GBP · VAT handled by Stripe · Test-mode card 4242 4242 4242 4242</p>
    </div>
  );
}
